import { useState, useRef, useMemo } from 'react';
import { FormControl, Stack, FormLabel, Text, Box } from '@chakra-ui/react';
import { Field } from 'formik';
import TextAreaField from '@/components/forms/new/formikFormFields/TextAreaField';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import PillSelector from 'components/forms/new/PillSelector';
import {
    stackSpacing,
    formLabelStyling,
    stackStyling,
    descriptionStyling,
} from 'components/forms/new/formStyles';
import { FORM_FIELD_PATHS } from '../constants.js';
import { useCustomSIPHeaderForm } from '../hooks/useCustomSIPHeaderForm.js';
import useGetCustomSIPHeaderOptions from '../queries/useGetCustomSIPHeaderOptions.jsx';

const CustomSIPHeaderValue = ({ darkMode, errors, touched }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { updateFormData } = useCustomSIPHeaderForm();
    const { data: optionsData } = useGetCustomSIPHeaderOptions();

    // Create refs for the textareas
    const valueTextareaRef = useRef(null);

    // Parse fields from API response and format as [field]
    const tcapValueButtons = useMemo(() => {
        if (!optionsData?.fields) return [];

        // Split comma-separated fields and wrap each in brackets
        return optionsData.fields
            .split(',')
            .map((field) => `[${field.trim()}]`)
            .filter((field) => field !== '[]'); // Remove any empty entries
    }, [optionsData]);

    const inputRefs = {
        [FORM_FIELD_PATHS.VALUE]: valueTextareaRef,
    };

    const handleInsert = (newValue, fieldName) => {
        updateFormData(fieldName, newValue);
    };

    return (
        <FormControl
            sx={{ margin: '0 !important' }}
            isInvalid={
                !!errors[FORM_FIELD_PATHS.VALUE] &&
                touched[FORM_FIELD_PATHS.VALUE]
            }
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={{
                    ...stackStyling(true),
                    height: '100%',
                    paddingBottom: '20px',
                }}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Value <span style={{ color: '#e53e3e' }}>*</span>
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth={250}>
                        Construct the format of the header value, using
                        available TCAP values and free text.
                    </Text>
                </Box>
                <Box width="100%" paddingTop="15px">
                    <Field
                        name={FORM_FIELD_PATHS.VALUE}
                        validate={(value) => {
                            if (!value || value === '') {
                                return 'Value is required';
                            }
                            if (value.length > 100) {
                                return 'Value must be 100 characters or less';
                            }
                        }}>
                        {({ field, form }) => (
                            <TextAreaField
                                field={{
                                    ...field,
                                    onChange: (e) => {
                                        const value = e.target.value;
                                        if (value.length <= 100) {
                                            field.onChange(e);
                                            updateFormData(
                                                FORM_FIELD_PATHS.VALUE,
                                                value.trim(),
                                            );
                                        }
                                    },
                                }}
                                form={form}
                                ref={valueTextareaRef}
                                id={FORM_FIELD_PATHS.VALUE}
                                rows={4}
                                maxLength={100}
                            />
                        )}
                    </Field>
                    <PillSelector
                        values={tcapValueButtons}
                        onInsert={handleInsert}
                        inputRefs={inputRefs}
                        darkMode={darkMode}
                        description="Click the below TCAP object values to dynamically populate the custom header value data."
                    />
                </Box>
                {isHovered && (
                    <FieldToolTip
                        label="Construct the header value with TCAP values"
                        ariaLabel="custom-sip-header-value-tip"
                        left="25px"
                        top="25px"
                    />
                )}
            </Stack>
        </FormControl>
    );
};

export default CustomSIPHeaderValue;
